import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, WarningCircle, TrendUp, ArrowsClockwise, CurrencyDollar, ChartBar, Lightning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (v, kind = 'usd') => {
  if (v === null || v === undefined) return '—';
  if (kind === 'usd') return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  if (kind === 'pct') return `${(Number(v) * 100).toFixed(1)}%`;
  if (kind === 'int') return Number(v).toLocaleString();
  return String(v);
};

const marginTone = (m, floor, warn, target) => {
  if (m === null || m === undefined) return 'text-slate-400';
  if (m < floor) return 'text-rose-400';
  if (m < warn) return 'text-amber-400';
  if (m < target) return 'text-yellow-300';
  return 'text-emerald-400';
};

const Card = ({ children, className = '' }) => (
  <div className={`rounded-xl border border-slate-700/60 bg-slate-900/60 backdrop-blur ${className}`}>{children}</div>
);

const Stat = ({ label, value, tone = 'text-white', testid }) => (
  <div className="p-4">
    <p className="text-slate-400 text-[10px] uppercase tracking-wider">{label}</p>
    <p className={`text-xl font-semibold ${tone} mt-1`} data-testid={testid}>{value}</p>
  </div>
);

const Pill = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    emerald: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
    amber: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    rose: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${tones[tone] || tones.slate}`}>{children}</span>;
};

const STATUS_TONE = {
  on_target: 'emerald',
  below_target: 'amber',
  below_warn: 'amber',
  below_floor: 'rose',
  increase_price: 'rose',
  review_price: 'amber',
  consider_lowering: 'cyan',
  above_review_ceiling: 'cyan',
  insufficient_data: 'slate',
};

const CostIntelligencePage = () => {
  const [tab, setTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState(null);
  const [providers, setProviders] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [packs, setPacks] = useState(null);
  const [stripe, setStripe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, a, p, r, c, sub, pk, st] = await Promise.all([
        axios.get(`${API_URL}/api/admin/cost-intelligence/summary`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/assets`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/providers`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/recommendations`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/customers`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/subscription`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/packs`, { headers: authHeaders() }),
        axios.get(`${API_URL}/api/admin/cost-intelligence/stripe-readiness`, { headers: authHeaders() }),
      ]);
      setSummary(s.data); setAssets(a.data); setProviders(p.data);
      setRecommendations(r.data); setCustomers(c.data);
      setSubscription(sub.data); setPacks(pk.data); setStripe(st.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to load Cost Intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshRecs = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/cost-intelligence/recommendations/refresh`, {}, { headers: authHeaders() });
      toast.success(`Refreshed: ${r.data.inserted} new · ${r.data.unchanged} unchanged`);
      await load();
    } catch (e) {
      toast.error('Refresh failed');
    } finally {
      setBusy(false);
    }
  };

  const decide = async (id, decision) => {
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/admin/cost-intelligence/recommendations/${id}/decision`,
        { decision }, { headers: authHeaders() });
      toast.success(`${decision}.`);
      await load();
    } catch (e) {
      toast.error('Decision failed');
    } finally {
      setBusy(false);
    }
  };

  const sendDigestNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/cost-intelligence/digest/send-now`, {}, { headers: authHeaders() });
      if (r.data.ok) toast.success(`Digest sent to ${r.data.detail?.sent || 0} recipients`);
      else toast.error(`Digest not sent: ${r.data.detail?.reason || 'unknown'}`);
    } catch (e) {
      toast.error('Digest send failed');
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-950 text-slate-100 p-8" data-testid="cost-intel-loading">Loading Cost Intelligence…</div>;
  }
  if (error) {
    return <div className="min-h-screen bg-slate-950 text-rose-300 p-8" data-testid="cost-intel-error">{error}</div>;
  }

  const s = summary || {};
  const marginToneFn = (m) => marginTone(m, s.floor_margin || 0.60, s.warn_margin_low || 0.70, s.target_margin || 0.75);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="cost-intelligence-page">
      <div className="max-w-7xl mx-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <ChartBar size={22} weight="fill" className="text-cyan-400" />
              Cost Intelligence <span className="text-slate-500 text-sm">Founder-only</span>
            </h1>
            <p className="text-slate-400 text-xs mt-1">Real production costs · target margin {fmt(s.target_margin, 'pct')} · floor {fmt(s.floor_margin, 'pct')}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={load}
              className="text-xs px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 hover:bg-slate-700 flex items-center gap-1"
              data-testid="ci-reload"
              disabled={busy}
            >
              <ArrowsClockwise size={12} /> Reload
            </button>
            <button
              onClick={sendDigestNow}
              className="text-xs px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/30 hover:bg-cyan-500/30"
              data-testid="ci-send-digest"
              disabled={busy}
            >
              Send digest now
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 overflow-x-auto" data-testid="ci-tabs">
          {[
            ['summary', 'Summary'],
            ['assets', 'Assets'],
            ['providers', 'Providers'],
            ['recommendations', 'Recommendations'],
            ['customers', 'Customers'],
            ['subscription', 'Subscription & Packs'],
            ['stripe', 'Stripe Readiness'],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`text-xs px-3 py-1.5 rounded-lg border ${tab === k ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-100' : 'bg-slate-900/40 border-slate-700 text-slate-300'}`}
              data-testid={`ci-tab-${k}`}
            >{label}</button>
          ))}
        </div>

        {tab === 'summary' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><Stat label="Provider spend today" value={fmt(s.provider_spend_today_usd)} testid="ci-spend-today" /></Card>
              <Card><Stat label={`Provider spend (${s.window_days}d)`} value={fmt(s.provider_spend_month_usd)} testid="ci-spend-month" /></Card>
              <Card><Stat label="Credit revenue" value={fmt(s.production_credit_revenue_usd)} testid="ci-revenue" /></Card>
              <Card><Stat label="Blended gross margin" value={fmt(s.blended_gross_margin, 'pct')} tone={marginToneFn(s.blended_gross_margin)} testid="ci-blended-margin" /></Card>
              <Card><Stat label="Active customers" value={fmt(s.active_customers, 'int')} testid="ci-active-users" /></Card>
              <Card><Stat label="Avg cost / customer" value={fmt(s.avg_cost_per_active_customer_usd)} testid="ci-avg-cost-per-user" /></Card>
              <Card><Stat label="Failed-generation cost" value={fmt(s.failed_generation_cost_usd)} tone="text-amber-300" testid="ci-failed-cost" /></Card>
              <Card><Stat label="Credits refunded" value={fmt(s.credits_refunded, 'int')} testid="ci-refunded" /></Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Card className="p-4">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider">Most expensive provider</p>
                <p className="text-white font-semibold mt-1">{s.most_expensive_provider?.provider || '—'}</p>
                <p className="text-slate-400 text-xs">{fmt(s.most_expensive_provider?.cost_usd)}</p>
              </Card>
              <Card className="p-4">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider">Most profitable asset</p>
                <p className="text-white font-semibold mt-1">{s.most_profitable_asset_type?.asset_kind || '—'}</p>
                <p className={`text-xs ${marginToneFn(s.most_profitable_asset_type?.margin)}`}>{fmt(s.most_profitable_asset_type?.margin, 'pct')} margin</p>
              </Card>
              <Card className="p-4">
                <p className="text-slate-400 text-[10px] uppercase tracking-wider">Lowest-margin asset</p>
                <p className="text-white font-semibold mt-1">{s.lowest_margin_asset_type?.asset_kind || '—'}</p>
                <p className={`text-xs ${marginToneFn(s.lowest_margin_asset_type?.margin)}`}>{fmt(s.lowest_margin_asset_type?.margin, 'pct')} margin</p>
              </Card>
            </div>
          </div>
        )}

        {tab === 'assets' && assets && (
          <Card className="overflow-x-auto" data-testid="ci-assets-table">
            <table className="min-w-full text-xs">
              <thead className="text-slate-400">
                <tr>
                  {['Asset', 'N', 'Real prod', 'Data quality', 'Avg cost', 'p50', 'p95', 'Conservative', 'Current cr', 'Revenue', 'Avg margin', 'p95 margin', 'Recommend', 'Status'].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-medium border-b border-slate-700/60">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-slate-200">
                {assets.rows.map(r => {
                  const mix = r.cost_source_mix || {};
                  const dq = mix.provider_reported > 0 ? 'provider' : mix.rate_card > 0 ? 'rate_card' : 'estimate';
                  const dqTone = dq === 'provider' ? 'emerald' : dq === 'rate_card' ? 'amber' : 'slate';
                  return (
                  <tr key={r.asset_kind} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                    <td className="px-3 py-2 font-medium" data-testid={`ci-asset-${r.asset_kind}`}>{r.asset_kind}</td>
                    <td className="px-3 py-2">{r.n}</td>
                    <td className="px-3 py-2" data-testid={`ci-asset-${r.asset_kind}-real-prod`}>
                      {r.n_real_production || 0}
                      <span className="text-slate-500 text-[10px] ml-1">
                        ({r.real_production_ratio !== null && r.real_production_ratio !== undefined ? `${(r.real_production_ratio * 100).toFixed(0)}%` : '0%'})
                      </span>
                    </td>
                    <td className="px-3 py-2"><Pill tone={dqTone}>{dq === 'provider' ? 'Provider' : dq === 'rate_card' ? 'Rate card' : 'Estimate'}</Pill></td>
                    <td className="px-3 py-2">{fmt(r.avg_cost_usd)}</td>
                    <td className="px-3 py-2">{fmt(r.p50_cost_usd)}</td>
                    <td className="px-3 py-2">{fmt(r.p95_cost_usd)}</td>
                    <td className="px-3 py-2">{fmt(r.conservative_cost_usd)}</td>
                    <td className="px-3 py-2">{r.current_credit_charge}</td>
                    <td className="px-3 py-2">{fmt(r.effective_revenue_usd)}</td>
                    <td className={`px-3 py-2 ${marginToneFn(r.avg_gross_margin)}`}>{fmt(r.avg_gross_margin, 'pct')}</td>
                    <td className={`px-3 py-2 ${marginToneFn(r.p95_gross_margin)}`}>{fmt(r.p95_gross_margin, 'pct')}</td>
                    <td className="px-3 py-2 font-medium">{r.recommended_credit_charge}</td>
                    <td className="px-3 py-2"><Pill tone={STATUS_TONE[r.recommendation_status] || 'slate'}>{r.recommendation_status}</Pill></td>
                  </tr>
                );})}
              </tbody>
            </table>
          </Card>
        )}

        {tab === 'providers' && providers && (
          <Card className="overflow-x-auto" data-testid="ci-providers-table">
            <table className="min-w-full text-xs">
              <thead className="text-slate-400">
                <tr>{['Provider', 'Calls', 'Success', 'Retry', 'Total $', 'Avg $/call', 'p95 latency', 'Trend Δ', 'Assets'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-medium border-b border-slate-700/60">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="text-slate-200">
                {providers.rows.map(p => (
                  <tr key={p.provider} className="border-b border-slate-800/60 hover:bg-slate-800/30" data-testid={`ci-provider-${p.provider}`}>
                    <td className="px-3 py-2 font-medium">{p.provider} <span className="text-slate-500 text-[10px]">{p.models.join(', ')}</span></td>
                    <td className="px-3 py-2">{p.calls}</td>
                    <td className="px-3 py-2">{fmt(p.success_rate, 'pct')}</td>
                    <td className="px-3 py-2">{fmt(p.retry_rate, 'pct')}</td>
                    <td className="px-3 py-2">{fmt(p.total_cost_usd)}</td>
                    <td className="px-3 py-2">{fmt(p.avg_cost_per_call_usd)}</td>
                    <td className="px-3 py-2">{p.p95_latency_ms ? `${p.p95_latency_ms}ms` : '—'}</td>
                    <td className={`px-3 py-2 ${p.cost_trend_pct === null ? 'text-slate-500' : p.cost_trend_pct > 0.10 ? 'text-amber-300' : 'text-emerald-300'}`}>
                      {p.cost_trend_pct === null ? '—' : `${(p.cost_trend_pct * 100).toFixed(1)}%`}
                    </td>
                    <td className="px-3 py-2 text-slate-400">{p.assets_affected.slice(0, 3).join(', ')}</td>
                  </tr>
                ))}
                {providers.rows.length === 0 && (
                  <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">No provider data yet in this window.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}

        {tab === 'recommendations' && recommendations && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-slate-400 text-xs">{recommendations.rows.length} total · pending require approval</p>
              <button onClick={refreshRecs} className="text-xs px-3 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/30" data-testid="ci-refresh-recs" disabled={busy}>Recompute</button>
            </div>
            <Card className="overflow-x-auto" data-testid="ci-recommendations-table">
              <table className="min-w-full text-xs">
                <thead className="text-slate-400">
                  <tr>{['Asset', 'Current', 'Recommend', 'Reason', 'Samples', 'Confidence', 'Projected margin', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left px-3 py-2 font-medium border-b border-slate-700/60">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="text-slate-200">
                  {recommendations.rows.map(r => {
                    const conf = r.confidence || 'low';
                    const confTone = conf === 'high' ? 'emerald' : conf === 'medium' ? 'amber' : 'slate';
                    const sup = r.supporting || {};
                    return (
                    <tr key={r.id} className="border-b border-slate-800/60" data-testid={`ci-rec-${r.id}`}>
                      <td className="px-3 py-2 font-medium">{r.asset_kind}</td>
                      <td className="px-3 py-2">{r.current_credits}</td>
                      <td className="px-3 py-2">{r.recommended_credits}</td>
                      <td className="px-3 py-2"><Pill tone={STATUS_TONE[r.reason] || 'slate'}>{r.reason}</Pill></td>
                      <td className="px-3 py-2">
                        <span title={`${sup.n_real_production || 0} real prod / ${sup.n || 0} total`}>
                          {sup.n_real_production || 0}<span className="text-slate-500">/{sup.n || 0}</span>
                        </span>
                      </td>
                      <td className="px-3 py-2"><Pill tone={confTone}>{conf}</Pill></td>
                      <td className={`px-3 py-2 ${marginToneFn(r.expected_resulting_margin)}`}>{fmt(r.expected_resulting_margin, 'pct')}</td>
                      <td className="px-3 py-2"><Pill tone={r.status === 'approved' ? 'emerald' : r.status === 'rejected' ? 'rose' : r.status === 'deferred' ? 'amber' : 'cyan'}>{r.status}</Pill></td>
                      <td className="px-3 py-2">
                        {r.status === 'pending' || r.status === 'deferred' ? (
                          <div className="flex gap-1">
                            <button onClick={() => decide(r.id, 'approved')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/30" data-testid={`ci-approve-${r.id}`} disabled={busy}>Approve</button>
                            <button onClick={() => decide(r.id, 'rejected')} className="text-[10px] px-2 py-0.5 rounded bg-rose-500/20 border border-rose-500/30" data-testid={`ci-reject-${r.id}`} disabled={busy}>Reject</button>
                            <button onClick={() => decide(r.id, 'deferred')} className="text-[10px] px-2 py-0.5 rounded bg-slate-500/20 border border-slate-500/30" data-testid={`ci-defer-${r.id}`} disabled={busy}>Defer</button>
                          </div>
                        ) : (
                          <span className="text-slate-500 text-[10px]">{r.approved_by || '—'} · {r.approved_at ? new Date(r.approved_at).toLocaleString() : ''}</span>
                        )}
                      </td>
                    </tr>
                  );})}
                  {recommendations.rows.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-500">No recommendations yet. Click Recompute to scan latest snapshots.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </div>
        )}

        {tab === 'customers' && customers && (
          <Card className="overflow-x-auto" data-testid="ci-customers-table">
            <table className="min-w-full text-xs">
              <thead className="text-slate-400">
                <tr>{['User', 'Assets', 'Credits', 'Refunded', 'Cost', 'Revenue', 'Margin'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-medium border-b border-slate-700/60">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="text-slate-200">
                {customers.rows.map(c => (
                  <tr key={c.user_id} className="border-b border-slate-800/60">
                    <td className="px-3 py-2 font-mono text-[10px]">{c.user_id.slice(0, 12)}…</td>
                    <td className="px-3 py-2">{c.assets}</td>
                    <td className="px-3 py-2">{c.credits_consumed}</td>
                    <td className="px-3 py-2">{c.credits_refunded}</td>
                    <td className="px-3 py-2">{fmt(c.cost_usd)}</td>
                    <td className="px-3 py-2">{fmt(c.revenue_usd)}</td>
                    <td className={`px-3 py-2 ${marginToneFn(c.gross_margin)}`}>{fmt(c.gross_margin, 'pct')}</td>
                  </tr>
                ))}
                {customers.rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-500">No customer data yet in this window.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}

        {tab === 'subscription' && subscription && packs && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Card className="p-4">
              <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><CurrencyDollar size={16} className="text-emerald-400" /> Subscription ($49/mo Strategist)</h3>
              <p className="text-slate-400 text-[11px] mb-3">Includes strategy, MI, OI, Business Brain, Brand Brain, 30 monthly credits.</p>
              <div className="space-y-1 text-xs">
                <p>Strategy provider cost / sub: <span className="text-white">{fmt(subscription.avg_strategy_provider_cost_per_sub_usd)}</span></p>
                <p>Live research cost / sub: <span className="text-white">{fmt(subscription.avg_live_research_cost_per_sub_usd)}</span></p>
                <p>Included credits cost / sub: <span className="text-white">{fmt(subscription.avg_included_credit_cost_per_sub_usd)}</span></p>
                <p className="font-medium mt-2">Total COGS / sub: <span className={marginToneFn((49 - subscription.total_cogs_per_sub_usd) / 49)}>{fmt(subscription.total_cogs_per_sub_usd)}</span></p>
                <p className="font-medium">Gross margin: <span className={marginToneFn(subscription.gross_margin_before_packs)}>{fmt(subscription.gross_margin_before_packs, 'pct')}</span></p>
              </div>
            </Card>
            <Card className="p-4">
              <h3 className="text-white font-semibold text-sm mb-2 flex items-center gap-2"><Lightning size={16} className="text-cyan-400" /> Credit Packs</h3>
              <ul className="text-xs text-slate-300 mb-3 space-y-0.5">
                {packs.packs.map(p => <li key={p.key}>{p.credits} credits — ${p.price_usd}</li>)}
              </ul>
              <div className="space-y-1 text-xs">
                <p>Credits consumed: <span className="text-white">{fmt(packs.credits_consumed, 'int')}</span></p>
                <p>Credit revenue realized: <span className="text-white">{fmt(packs.credit_revenue_realized_usd)}</span></p>
                <p>Cost of consumed credits: <span className="text-white">{fmt(packs.credit_cost_realized_usd)}</span></p>
                <p>Avg cost / consumed credit: <span className="text-white">{fmt(packs.avg_cost_per_consumed_credit_usd)}</span></p>
                <p>Unused purchased liability: <span className="text-amber-300">{packs.unused_purchased_credit_liability_credits} cr · {fmt(packs.unused_purchased_credit_liability_usd)}</span></p>
                <p className="font-medium">Realized pack margin: <span className={marginToneFn(packs.realized_gross_margin_on_consumed_credits)}>{fmt(packs.realized_gross_margin_on_consumed_credits, 'pct')}</span></p>
              </div>
            </Card>
          </div>
        )}

        {tab === 'stripe' && stripe && (
          <div className="space-y-3">
            <Card className="p-4">
              <div className="flex items-center gap-2">
                {stripe.recommendation === 'GO' ? <CheckCircle size={18} weight="fill" className="text-emerald-400" /> : <WarningCircle size={18} weight="fill" className="text-amber-400" />}
                <h3 className="text-white font-semibold text-sm">Stripe recommendation: <span className={stripe.recommendation === 'GO' ? 'text-emerald-300' : 'text-amber-300'}>{stripe.recommendation}</span></h3>
              </div>
              {stripe.missing_envs.length > 0 && (
                <p className="text-amber-300 text-xs mt-2">Missing envs: {stripe.missing_envs.join(', ')}</p>
              )}
              {stripe.assets_below_floor.length > 0 && (
                <p className="text-rose-300 text-xs mt-1">Assets below {fmt(stripe.floor_margin, 'pct')} margin: {stripe.assets_below_floor.map(a => a.asset_kind).join(', ')}</p>
              )}
            </Card>
            <Card className="p-4">
              <h4 className="text-white font-semibold text-xs mb-2">Recommended Stripe products</h4>
              <ul className="text-xs text-slate-300 space-y-1">
                {stripe.recommended_products.map(p => (
                  <li key={p.key}>
                    <b>{p.key}</b> ({p.type}) — ${p.price_usd}{p.credits ? ` · ${p.credits} credits` : ''}{p.trial_days ? ` · ${p.trial_days}-day trial` : ''} · env: <code className="text-cyan-300">{p.env_var}</code>
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-4">
              <h4 className="text-white font-semibold text-xs mb-2">Preview test plan</h4>
              <ol className="text-xs text-slate-300 list-decimal ml-4 space-y-1">{stripe.preview_test_plan.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </Card>
            <Card className="p-4">
              <h4 className="text-white font-semibold text-xs mb-2">Production activation plan</h4>
              <ol className="text-xs text-slate-300 list-decimal ml-4 space-y-1">{stripe.production_activation_plan.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </Card>
            <Card className="p-4">
              <h4 className="text-white font-semibold text-xs mb-2">Rollback plan</h4>
              <ol className="text-xs text-slate-300 list-decimal ml-4 space-y-1">{stripe.rollback_plan.map((s, i) => <li key={i}>{s}</li>)}</ol>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CostIntelligencePage;
